import { IndicatorData } from '../TechnicalAnalysisTradingView';

export interface OhlcRow {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export function calculateBuySellFrequency(data: OhlcRow[], bidAskData: any[], period: number = 14): { buyFreq: IndicatorData[], sellFreq: IndicatorData[] } {
  const buyFreq: IndicatorData[] = [];
  const sellFreq: IndicatorData[] = [];
  
  console.log('📊 BuySellFrequency: Starting calculation with:', {
    ohlcDataLength: data.length,
    bidAskDataLength: bidAskData?.length || 0,
    period,
    sampleBidAskData: bidAskData?.slice(0, 3).map(row => ({
      time: row.Time || row.time,
      bidCount: row.BidCount,
      askCount: row.AskCount,
      bidVolume: row.BidVolume || row.BLot,
      askVolume: row.AskVolume || row.SLot,
      price: row.Price,
      date: row.date
    })) || []
  });
  
  if (!bidAskData || bidAskData.length === 0) {
    console.log('📊 BuySellFrequency: No bid/ask data available - this is normal if no trading data exists');
    return { buyFreq, sellFreq };
  }
  
  // If no OHLC data, create time points from bid/ask data (same pattern as FootprintChart)
  if (!data || data.length === 0) {
    console.log('📊 BuySellFrequency: No OHLC data, creating time points from bid/ask data');
    
    // Group bid/ask data by date (same as FootprintChart)
    const dataByDate: { [date: string]: any[] } = {};
    
    bidAskData.forEach(row => {
      const date = row.date || 'unknown';
      if (!dataByDate[date]) {
        dataByDate[date] = [];
      }
      dataByDate[date].push(row);
    });
    
    // Calculate buy/sell frequency for each date
    Object.entries(dataByDate).forEach(([date, dayData]) => {
      if (dayData.length === 0) return;
      
      // Calculate total bid and ask counts for the day
      const totalBidCount = dayData.reduce((sum, row) => sum + (row.BidCount || 0), 0);
      const totalAskCount = dayData.reduce((sum, row) => sum + (row.AskCount || 0), 0);
      
      if (totalBidCount > 0 || totalAskCount > 0) {
        // Create timestamp for midday of the date
        const dateTime = new Date(date.slice(0, 4) + '-' + date.slice(4, 6) + '-' + date.slice(6, 8) + 'T12:00:00Z').getTime() / 1000;
        
        buyFreq.push({
          time: dateTime,
          value: totalBidCount
        });
        
        sellFreq.push({
          time: dateTime,
          value: totalAskCount
        });
        
        console.log(`📊 BuySellFrequency: Daily data for ${date}:`, {
          date: date,
          totalBidCount: totalBidCount,
          totalAskCount: totalAskCount,
          recordsCount: dayData.length,
          dateTime: dateTime,
          dateTimeDate: new Date(dateTime * 1000).toISOString()
        });
      }
    });
    // Pre-group bid/ask records by date string (YYYYMMDD) for O(1) lookup
    const bidAskByDate = new Map<string, any[]>();
    for (let i = 0; i < bidAskData.length; i++) {
      const row = bidAskData[i];
      const dStr = row.date || '';
      if (dStr) {
        let list = bidAskByDate.get(dStr);
        if (!list) {
          list = [];
          bidAskByDate.set(dStr, list);
        }
        list.push(row);
      }
    }

    // Convert OHLC data to CandleData format with footprint volume levels
    data.forEach(candle => {
      const dateObj = new Date(candle.time * 1000);
      const yyyy = dateObj.getUTCFullYear();
      const mm = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dateObj.getUTCDate()).padStart(2, '0');
      const candleDate = `${yyyy}${mm}${dd}`;
      
      const dayBidAsk = bidAskByDate.get(candleDate);
      if (!dayBidAsk || dayBidAsk.length === 0) return;
      
      let totalBidCount = 0;
      let totalAskCount = 0;
      for (let k = 0; k < dayBidAsk.length; k++) {
        const row = dayBidAsk[k];
        if (row.Price >= candle.low && row.Price <= candle.high) {
          totalBidCount += (row.BidCount || 0);
          totalAskCount += (row.AskCount || 0);
        }
      }
      
      if (totalBidCount > 0 || totalAskCount > 0) {
        buyFreq.push({
          time: candle.time,
          value: totalBidCount
        });
        
        sellFreq.push({
          time: candle.time,
          value: totalAskCount
        });
      }
    });
  }
  
  // Sort by time and remove duplicates
  buyFreq.sort((a, b) => a.time - b.time);
  sellFreq.sort((a, b) => a.time - b.time);
  
  // Remove duplicate time entries and ensure unique timestamps
  const uniqueBuyFreq: IndicatorData[] = [];
  const uniqueSellFreq: IndicatorData[] = [];
  
  const seenTimes = new Set<number>();
  
  // Process buy frequency data
  buyFreq.forEach(point => {
    if (point.time && point.value !== undefined && point.value !== null && !seenTimes.has(point.time)) {
      seenTimes.add(point.time);
      uniqueBuyFreq.push(point);
    }
  });
  
  // Reset seen times for sell frequency
  seenTimes.clear();
  
  // Process sell frequency data
  sellFreq.forEach(point => {
    if (point.time && point.value !== undefined && point.value !== null && !seenTimes.has(point.time)) {
      seenTimes.add(point.time);
      uniqueSellFreq.push(point);
    }
  });
  
  // Ensure we have valid data points for line plotting
  const validBuyFreq = uniqueBuyFreq;
  const validSellFreq = uniqueSellFreq;
  
  // If no valid data, create empty arrays to prevent chart errors
  if (validBuyFreq.length === 0 && validSellFreq.length === 0) {
    console.log('📊 BuySellFrequency: No valid data points found, returning empty arrays');
    return { buyFreq: [], sellFreq: [] };
  }
  
  // Log data range for debugging
  const dataRange = validBuyFreq.length > 0 ? {
    startDate: new Date((validBuyFreq[0]?.time || 0) * 1000).toISOString(),
    endDate: new Date((validBuyFreq[validBuyFreq.length - 1]?.time || 0) * 1000).toISOString(),
    totalPoints: validBuyFreq.length,
    duplicateTimesRemoved: buyFreq.length - validBuyFreq.length
  } : null;

  console.log('📊 BuySellFrequency: Generated data:', {
    buyFreqLength: validBuyFreq.length,
    sellFreqLength: validSellFreq.length,
    dataRange,
    sampleData: validBuyFreq.slice(0, 3),
    duplicateTimesRemoved: buyFreq.length - validBuyFreq.length
  });
  
  return { buyFreq: validBuyFreq, sellFreq: validSellFreq };
}
